import type { Room } from '@hollowveil/shared';

export const roomForbiddenLibrary: Room = {
      id: 'room_forbidden_library',
      name: "La Bibliothèque Interdite",
      description: "Une bibliothèque hantée où les livres spectraux révèlent des indices pour les énigmes du Bibliothécaire.",
      size: [32, 14, 32],
      floorStyle: 'wood',
      ambiance: {
        realWorld: {
          backgroundColor: '#3e2723',
          ambientLightColor: '#8d6e63',
          ambientLightIntensity: 0.5,
        },
        spectralWorld: {
          backgroundColor: '#1a1a2e',
          ambientLightColor: '#9c27b0',
          ambientLightIntensity: 0.35,
        },
      },
      objects: [
        // Bookshelves
        {
          id: 'obj_bookshelf_1',
          name: 'Étagère',
          type: 'static',
          modelId: 'wall',
          position: [2, 0, 2],
          rotation: [0, 0, 0],
          scale: [0.5, 3, 4],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_bookshelf_2',
          name: 'Étagère',
          type: 'static',
          modelId: 'wall',
          position: [2, 0, 10],
          rotation: [0, 0, 0],
          scale: [0.5, 3, 4],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_bookshelf_3',
          name: 'Étagère',
          type: 'static',
          modelId: 'wall',
          position: [14, 0, 6],
          rotation: [0, 0, 0],
          scale: [0.5, 3, 6],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Book of Origins (spectral - gives clue 1)
        {
          id: 'obj_book_origins',
          name: 'Tome des Origines',
          type: 'interactive',
          modelId: 'block',
          position: [4, 1.2, 3],
          rotation: [0, 0.2, 0],
          scale: [0.5, 0.1, 0.3],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'wood' },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: '"Le Tome des Origines parle de la création du Voile...\n\n📖 Indice pour la 1ère énigme du Bibliothécaire :\nLe premier mot est SILENCE - car le Voile fut créé dans le silence absolu."',
              },
            },
          ],
        },
        // Book of Elements (spectral - gives clue 2)
        {
          id: 'obj_book_elements',
          name: 'Grimoire des Éléments',
          type: 'interactive',
          modelId: 'block',
          position: [4, 1.2, 11],
          rotation: [0, -0.1, 0],
          scale: [0.5, 0.1, 0.3],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'wood' },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: '"Le Grimoire des Éléments décrit les forces primordiales...\n\n📖 Indice pour la 2ème énigme du Bibliothécaire :\nLe deuxième mot est FEU - l\'élément qui détruit et purifie."',
              },
            },
          ],
        },
        // Hidden book (spectral - gives clue 3)
        {
          id: 'obj_book_secrets',
          name: 'Codex des Secrets',
          type: 'interactive',
          modelId: 'block',
          position: [12, 1.5, 7],
          rotation: [0, 0.5, 0],
          scale: [0.4, 0.1, 0.25],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'accent' },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: '"Le Codex des Secrets révèle les vérités cachées...\n\n📖 Indice pour la 3ème énigme du Bibliothécaire :\nLe troisième mot est AME - ce qui traverse le Voile."',
              },
            },
          ],
        },
        // The Librarian (real world) - asks riddles
        {
          id: 'obj_librarian',
          name: 'Le Bibliothécaire',
          type: 'interactive',
          modelId: 'sage',
          position: [8, 0, 8],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'real',
          interactableInWorld: 'real',
          properties: {},
          onInteract: [
            {
              type: 'showRiddle',
              params: {
                riddle: {
                  id: 'riddle_librarian',
                  npcId: 'obj_librarian',
                  npcName: 'Le Bibliothécaire',
                  question: '"Bienvenue dans ma bibliothèque, voyageur.\n\nPour accéder au coffre secret, tu dois répondre à MES trois énigmes.\nMais les réponses... elles sont cachées dans les livres de l\'autre monde.\n\n🔮 PREMIÈRE ÉNIGME :\nJe suis ce que tu entends quand il n\'y a rien.\nJe règne dans la bibliothèque.\nQue suis-je ?"',
                  hint: 'Cherche dans le Tome des Origines, dans le monde spectral...',
                  type: 'text_input',
                  expectedAnswers: ['silence', 'le silence', 'quietude', 'calme'],
                  correctResponse: '"SILENCE... Exact. Le silence est la première clé. Va maintenant chercher la seconde réponse dans les livres spectraux..."',
                  incorrectResponse: '"Non... Les livres de l\'autre monde contiennent la réponse. Cherche dans le Tome des Origines."',
                  canRetry: true,
                  onCorrect: [
                    {
                      type: 'addToInventory',
                      params: {
                        itemId: 'librarian_key_1',
                        itemName: 'Première Clé du Savoir',
                        itemDescription: 'Une clé immatérielle représentant la première réponse.',
                        itemIcon: 'key',
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
        // Second riddle NPC (appears after first is solved)
        {
          id: 'obj_librarian_2',
          name: 'Le Bibliothécaire',
          type: 'interactive',
          modelId: 'sage',
          position: [10, 0, 4],
          rotation: [0, Math.PI, 0],
          scale: [1.1, 1.1, 1.1],
          visibleInWorld: 'real',
          interactableInWorld: 'real',
          properties: {},
          onInteract: [
            {
              type: 'showRiddle',
              params: {
                riddle: {
                  id: 'riddle_librarian_2',
                  npcId: 'obj_librarian_2',
                  npcName: 'Le Bibliothécaire',
                  question: '"🔮 DEUXIÈME ÉNIGME :\n\nJe danse sans jambes.\nJe consume sans bouche.\nJe donne la vie et la mort.\nQuel élément suis-je ?"',
                  hint: 'Cherche dans le Grimoire des Éléments...',
                  type: 'text_input',
                  expectedAnswers: ['feu', 'le feu', 'fire', 'flamme', 'flammes'],
                  correctResponse: '"FEU... L\'élément de transformation. Deux clés sur trois. Continue ta quête..."',
                  incorrectResponse: '"Non... Le Grimoire des Éléments dans l\'autre monde contient la réponse."',
                  canRetry: true,
                  onCorrect: [
                    {
                      type: 'addToInventory',
                      params: {
                        itemId: 'librarian_key_2',
                        itemName: 'Deuxième Clé du Savoir',
                        itemDescription: 'Une clé immatérielle représentant la deuxième réponse.',
                        itemIcon: 'key',
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
        // Third riddle NPC
        {
          id: 'obj_librarian_3',
          name: 'Le Bibliothécaire',
          type: 'interactive',
          modelId: 'sage',
          position: [6, 0, 12],
          rotation: [0, -Math.PI / 2, 0],
          scale: [1.1, 1.1, 1.1],
          visibleInWorld: 'real',
          interactableInWorld: 'real',
          properties: {},
          onInteract: [
            {
              type: 'showRiddle',
              params: {
                riddle: {
                  id: 'riddle_librarian_3',
                  npcId: 'obj_librarian_3',
                  npcName: 'Le Bibliothécaire',
                  question: '"🔮 TROISIÈME ET DERNIÈRE ÉNIGME :\n\nJe suis ce qui reste quand le corps s\'en va.\nJe traverse le Voile entre les mondes.\nJe suis immortelle mais invisible.\nQue suis-je ?"',
                  hint: 'Cherche dans le Codex des Secrets...',
                  type: 'text_input',
                  expectedAnswers: ['ame', 'âme', 'l\'ame', 'l\'âme', 'une ame', 'une âme', 'soul', 'esprit'],
                  correctResponse: '"L\'ÂME... Tu as trouvé les trois réponses. Le coffre secret t\'attend maintenant."',
                  incorrectResponse: '"Non... Le Codex des Secrets dans l\'autre monde révèle la réponse finale."',
                  canRetry: true,
                  onCorrect: [
                    {
                      type: 'openDoor',
                      params: {
                        doorId: 'obj_vault_door',
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
        // Vault door
        {
          id: 'obj_vault_door',
          name: 'Porte du Coffre',
          type: 'static',
          modelId: 'animated_door',
          position: [8, 0, 14],
          rotation: [0, 0, 0],
          scale: [1.2, 1.2, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Library vault - the reward
        {
          id: 'obj_library_vault',
          name: 'Coffre Secret',
          type: 'interactive',
          modelId: 'chest',
          position: [8, 0, 15.5],
          rotation: [0, 0, 0],
          scale: [1.2, 1.2, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
          onInteract: [
            {
              type: 'addToInventory',
              params: {
                itemId: 'librarian_scroll',
                itemName: 'Parchemin du Bibliothécaire',
                itemDescription: 'Un parchemin ancien contenant la sagesse de la bibliothèque. Il révèle les secrets des constellations.',
                itemIcon: 'scroll',
              },
            },
            {
              type: 'showMessage',
              params: {
                message: 'Tu as obtenu le Parchemin du Bibliothécaire ! Il contient les secrets des constellations.',
              },
            },
          ],
        },
        // Reading tables
        {
          id: 'obj_reading_table',
          name: 'Table de Lecture',
          type: 'static',
          modelId: 'block',
          position: [8, 0, 6],
          rotation: [0, 0, 0],
          scale: [2, 0.8, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { variant: 'wood' },
        },
        // Floating spectral books
        {
          id: 'obj_floating_book_1',
          name: 'Livre Flottant',
          type: 'static',
          modelId: 'block',
          position: [5, 2, 5],
          rotation: [0.2, 0.5, 0.1],
          scale: [0.4, 0.08, 0.3],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'accent' },
        },
        {
          id: 'obj_floating_book_2',
          name: 'Livre Flottant',
          type: 'static',
          modelId: 'block',
          position: [11, 2.5, 9],
          rotation: [-0.1, 1.2, 0.15],
          scale: [0.35, 0.08, 0.25],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'accent' },
        },
        // === Additional decorations for enlarged library (32x32) ===
        // More bookshelves
        {
          id: 'obj_bookshelf_4',
          name: 'Bibliothèque',
          type: 'static',
          modelId: 'bookshelf',
          position: [26, 0, 4],
          rotation: [0, 0, 0],
          scale: [1.2, 1.2, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_bookshelf_5',
          name: 'Bibliothèque',
          type: 'static',
          modelId: 'bookshelf',
          position: [26, 0, 12],
          rotation: [0, 0, 0],
          scale: [1.2, 1.2, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_bookshelf_6',
          name: 'Bibliothèque',
          type: 'static',
          modelId: 'bookshelf',
          position: [18, 0, 28],
          rotation: [0, Math.PI / 2, 0],
          scale: [1.2, 1.2, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_bookshelf_7',
          name: 'Bibliothèque',
          type: 'static',
          modelId: 'bookshelf',
          position: [6, 0, 24],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Reading tables
        {
          id: 'obj_table_1',
          name: 'Table',
          type: 'static',
          modelId: 'table',
          position: [20, 0, 8],
          rotation: [0, 0, 0],
          scale: [1.2, 1, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_table_2',
          name: 'Table',
          type: 'static',
          modelId: 'table',
          position: [12, 0, 22],
          rotation: [0, Math.PI / 4, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Chairs
        {
          id: 'obj_library_chair_1',
          name: 'Chaise',
          type: 'static',
          modelId: 'chair',
          position: [19, 0, 7],
          rotation: [0, Math.PI, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_library_chair_2',
          name: 'Chaise',
          type: 'static',
          modelId: 'chair',
          position: [21, 0, 9],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_library_chair_3',
          name: 'Chaise',
          type: 'static',
          modelId: 'chair',
          position: [11, 0, 21],
          rotation: [0, -Math.PI / 4, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Lanterns
        {
          id: 'obj_library_lantern_1',
          name: 'Lanterne',
          type: 'static',
          modelId: 'lantern',
          position: [4, 0, 16],
          rotation: [0, 0, 0],
          scale: [1.2, 1.2, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_library_lantern_2',
          name: 'Lanterne',
          type: 'static',
          modelId: 'lantern',
          position: [22, 0, 18],
          rotation: [0, 0, 0],
          scale: [1.2, 1.2, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_library_lantern_3',
          name: 'Lanterne',
          type: 'static',
          modelId: 'lantern',
          position: [28, 0, 26],
          rotation: [0, 0, 0],
          scale: [1.2, 1.2, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Candles on tables
        {
          id: 'obj_library_candles_1',
          name: 'Bougies',
          type: 'static',
          modelId: 'candles',
          position: [20, 0.8, 8],
          rotation: [0, 0.3, 0],
          scale: [0.8, 0.8, 0.8],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_library_candles_2',
          name: 'Bougies',
          type: 'static',
          modelId: 'candles',
          position: [8, 0.4, 6],
          rotation: [0, 0, 0],
          scale: [0.9, 0.9, 0.9],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Rugs
        {
          id: 'obj_library_rug_1',
          name: 'Tapis',
          type: 'static',
          modelId: 'rug',
          position: [8, 0.02, 8],
          rotation: [0, 0, 0],
          scale: [3, 1, 4],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_library_rug_2',
          name: 'Tapis',
          type: 'static',
          modelId: 'rug',
          position: [22, 0.02, 22],
          rotation: [0, Math.PI / 6, 0],
          scale: [2, 1, 3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Pillars
        {
          id: 'obj_library_pillar_1',
          name: 'Pilier',
          type: 'static',
          modelId: 'column',
          position: [16, 0, 4],
          rotation: [0, 0, 0],
          scale: [1, 1.3, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_library_pillar_2',
          name: 'Pilier',
          type: 'static',
          modelId: 'column',
          position: [16, 0, 16],
          rotation: [0, 0, 0],
          scale: [1, 1.3, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Statues
        {
          id: 'obj_library_statue',
          name: 'Statue du Savant',
          type: 'static',
          modelId: 'statue',
          position: [28, 0, 8],
          rotation: [0, -Math.PI / 2, 0],
          scale: [1, 1.2, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Pots
        {
          id: 'obj_library_pot_1',
          name: 'Pot',
          type: 'static',
          modelId: 'pot',
          position: [2, 0, 20],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_library_pot_2',
          name: 'Pot',
          type: 'static',
          modelId: 'pot',
          position: [30, 0, 14],
          rotation: [0, 0.5, 0],
          scale: [0.9, 0.9, 0.9],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Mirror
        {
          id: 'obj_library_mirror',
          name: 'Miroir Ancien',
          type: 'static',
          modelId: 'mirror',
          position: [30, 0, 2],
          rotation: [0, -Math.PI / 4, 0],
          scale: [1.2, 1.5, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Spectral orbs
        {
          id: 'obj_library_orb_1',
          name: 'Orbe Spectral',
          type: 'static',
          modelId: 'orb',
          position: [24, 2.5, 6],
          rotation: [0, 0, 0],
          scale: [0.4, 0.4, 0.4],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_library_orb_2',
          name: 'Orbe Spectral',
          type: 'static',
          modelId: 'orb',
          position: [8, 2, 26],
          rotation: [0, 0, 0],
          scale: [0.45, 0.45, 0.45],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_library_orb_3',
          name: 'Orbe Spectral',
          type: 'static',
          modelId: 'orb',
          position: [20, 1.8, 28],
          rotation: [0, 0, 0],
          scale: [0.35, 0.35, 0.35],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        // Floating books
        {
          id: 'obj_floating_book_3',
          name: 'Livre Flottant',
          type: 'static',
          modelId: 'block',
          position: [18, 2.2, 14],
          rotation: [0.15, 2.1, -0.1],
          scale: [0.4, 0.08, 0.28],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'accent' },
        },
        {
          id: 'obj_floating_book_4',
          name: 'Livre Flottant',
          type: 'static',
          modelId: 'block',
          position: [6, 2.8, 18],
          rotation: [-0.2, 0.8, 0.05],
          scale: [0.38, 0.08, 0.26],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'accent' },
        },
      ],
      puzzles: [
        {
          id: 'puzzle_library_riddles',
          name: 'Les Énigmes du Bibliothécaire',
          type: 'riddles',
          state: 'active',
          description: 'Trouve les indices dans les livres spectraux pour répondre aux énigmes.',
          hint: 'Les livres dans le monde spectral contiennent les réponses.',
          linkedObjectIds: ['obj_book_origins', 'obj_book_elements', 'obj_book_secrets', 'obj_librarian'],
          solution: {
            type: 'sequence',
            expected: ['silence', 'feu', 'ame'],
          },
          onSolve: [
            {
              type: 'showMessage',
              params: {
                message: 'Le Bibliothécaire approuve. Le coffre est accessible.',
              },
            },
          ],
        },
      ],
      connections: [
        {
          targetRoomId: 'room_crystal_labyrinth',
          position: [0, 0, 8],
          direction: 'west',
          locked: false,
        },
        {
          targetRoomId: 'room_celestial_observatory',
          position: [16, 0, 8],
          direction: 'east',
          locked: false,
        },
      ],
      initialWorld: 'real',
      hasSpectralLayer: true,
      palette: 'library',
      biome: 'forest',
      weather: 'drizzle',
      weatherIntensity: 0.3,
      npcs: [
        {
          id: 'npc_owl_library',
          type: 'owl',
          name: 'Hibou Bibliothécaire',
          position: [24, 3, 10],
          scale: 3,
          idleAnimation: 'sit',
          petAnimation: 'chirp',
          petRadius: 3,
          petCooldown: 3000,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_owl_library_2',
          type: 'owl',
          name: 'Chouette Savante',
          position: [8, 2.5, 8],
          scale: 2.5,
          idleAnimation: 'sit',
          petAnimation: 'chirp',
          petRadius: 2.5,
          petCooldown: 3000,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_fox_library',
          type: 'fox',
          name: 'Renard Lecteur',
          position: [10, 0, 24],
          scale: 3,
          idleAnimation: 'sleep',
          petAnimation: 'nuzzle',
          petRadius: 2.5,
          petCooldown: 2500,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_deer_library',
          type: 'deer',
          name: 'Biche des Contes',
          position: [26, 0, 26],
          scale: 3,
          idleAnimation: 'stand',
          petAnimation: 'nuzzle',
          petRadius: 3,
          petCooldown: 3000,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_squirrel_library',
          type: 'squirrel',
          name: 'Écureuil Archiviste',
          position: [18, 0, 14],
          scale: 2.5,
          idleAnimation: 'sit',
          petAnimation: 'jump',
          petRadius: 2,
          petCooldown: 2000,
          isPettable: true,
          visibleInWorld: 'both',
        },
      ],
};
